<template>
  <el-dialog
    title="选择国家或区域"
    :visible.sync="dialogVisible"
    width="800px"
    custom-class="country-region-picker-dialog"
    append-to-body
    :close-on-click-modal="false"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="selected-bar">
      <span class="selected-label">已选择区域（{{ draftSelected.length }}/{{ max }}）</span>
      <div v-if="draftSelected.length" class="selected-tags">
        <el-tag
          v-for="id in draftSelected"
          :key="id"
          closable
          size="small"
          class="selected-tag"
          @close="removeSelected(id)"
        >{{ getItemName(id) }}</el-tag>
      </div>
    </div>

    <div class="picker-content">
      <section class="picker-section">
        <div class="section-title">区域</div>
        <div class="option-grid">
          <div
            v-for="item in regionList"
            :key="item.id"
            class="option-item"
            :class="{ active: isSelected(item.id) }"
            @click="toggleSelect(item.id)"
          >{{ item.name }}</div>
        </div>
      </section>

      <section class="picker-section">
        <div class="section-title">热门国家或地区</div>
        <div class="option-grid">
          <div
            v-for="item in hotList"
            :key="item.id"
            class="option-item"
            :class="{ active: isSelected(item.id) }"
            @click="toggleSelect(item.id)"
          >{{ item.name }}</div>
        </div>
      </section>

      <section class="picker-section">
        <div class="section-title">国家或地区</div>
        <div class="letter-index">
          <div
            v-for="letter in letterList"
            :key="letter"
            class="letter-item"
            :class="{ active: activeLetter === letter, disabled: !hasLetterGroup(letter) }"
            @click="selectLetter(letter)"
          >{{ letter }}</div>
        </div>
        <div v-if="activeLetter" class="letter-group">
          <div class="letter-group__title">{{ activeLetter }}</div>
          <div class="option-grid">
            <div
              v-for="item in activeCountryList"
              :key="item.id"
              class="option-item"
              :class="{ active: isSelected(item.id) }"
              @click="toggleSelect(item.id)"
            >{{ item.name }}</div>
          </div>
        </div>
      </section>
    </div>

    <div slot="footer" class="picker-footer">
      <el-button class="btn-reset" @click="handleReset">重置</el-button>
      <el-button type="primary" class="btn-confirm" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

export default {
  name: 'CountryRegionPicker',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => []
    },
    max: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      draftSelected: [],
      countryData: null,
      itemMap: {},
      activeLetter: 'A',
      loading: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    letterList() {
      return LETTERS
    },
    regionList() {
      return (this.countryData && this.countryData.regions) || []
    },
    hotList() {
      return (this.countryData && this.countryData.countries_hot) || []
    },
    activeCountryList() {
      if (!this.countryData || !this.activeLetter) return []
      const group = this.countryData.countries_group || {}
      return group[this.activeLetter] || []
    }
  },
  methods: {
    async handleOpen() {
      this.draftSelected = (this.value || []).map(String)
      await this.loadCountryData()
      this.initActiveLetter()
    },
    handleClosed() {
      this.draftSelected = (this.value || []).map(String)
    },
    async loadCountryData() {
      if (this.countryData) return
      this.loading = true
      try {
        const { data: res } = await this.$http.get('/api/Common/getCountry')
        if (res.errorCode === 200) {
          this.countryData = res.data
          this.buildItemMap()
        }
      } finally {
        this.loading = false
      }
    },
    buildItemMap() {
      const map = {}
      const addItems = list => {
        ;(list || []).forEach(item => {
          map[String(item.id)] = item
        })
      }
      addItems(this.regionList)
      addItems(this.hotList)
      const group = (this.countryData && this.countryData.countries_group) || {}
      Object.keys(group).forEach(letter => addItems(group[letter]))
      this.itemMap = map
    },
    initActiveLetter() {
      const group = (this.countryData && this.countryData.countries_group) || {}
      const firstLetter = LETTERS.find(letter => (group[letter] || []).length)
      this.activeLetter = firstLetter || 'A'
    },
    hasLetterGroup(letter) {
      const group = (this.countryData && this.countryData.countries_group) || {}
      return !!(group[letter] && group[letter].length)
    },
    selectLetter(letter) {
      if (!this.hasLetterGroup(letter)) return
      this.activeLetter = letter
    },
    getItemName(id) {
      const item = this.itemMap[String(id)]
      return item ? item.name : id
    },
    isSelected(id) {
      return this.draftSelected.includes(String(id))
    },
    toggleSelect(id) {
      const key = String(id)
      const index = this.draftSelected.indexOf(key)
      if (index > -1) {
        this.draftSelected.splice(index, 1)
        return
      }
      if (this.draftSelected.length >= this.max) {
        this.$message.warning(`最多选择${this.max}个区域`)
        return
      }
      this.draftSelected.push(key)
    },
    removeSelected(id) {
      const key = String(id)
      this.draftSelected = this.draftSelected.filter(item => item !== key)
    },
    handleReset() {
      this.draftSelected = []
    },
    handleConfirm() {
      this.$emit('input', [...this.draftSelected])
      this.$emit('confirm', [...this.draftSelected])
      this.dialogVisible = false
    }
  }
}
</script>

<style lang="less">
.country-region-picker-dialog {
  .el-dialog__header {
    padding: 20px 24px 12px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .el-dialog__body {
    max-height: 70vh;
    padding: 0 24px 8px;
    overflow-y: auto;
  }

  .el-dialog__footer {
    padding: 16px 24px 24px;
  }
}
</style>

<style lang="less" scoped>
.selected-bar {
  margin: 16px 0;
  padding: 12px 16px;
  background: #eef6ff;
  border-radius: 4px;

  .selected-label {
    display: block;
    margin-bottom: 8px;
    color: #606266;
    font-size: 14px;
  }

  .selected-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .selected-tag {
    color: #409eff;
    background: #fff;
    border-color: #d9ecff;
  }
}

.picker-content {
  padding-bottom: 8px;
}

.picker-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  margin-bottom: 12px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.option-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.option-item {
  min-width: 88px;
  padding: 8px 14px;
  color: #303133;
  font-size: 14px;
  text-align: center;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #c6e2ff;
    color: #409eff;
  }

  &.active {
    color: #409eff;
    border-color: #409eff;
    background: #f5f9ff;
  }
}

.letter-index {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.letter-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #606266;
  font-size: 13px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(.disabled) {
    border-color: #409eff;
    color: #409eff;
  }

  &.active {
    color: #fff;
    background: #409eff;
    border-color: #409eff;
  }

  &.disabled {
    color: #c0c4cc;
    background: #f5f7fa;
    cursor: not-allowed;
  }
}

.letter-group {
  max-height: 220px;
  padding-right: 4px;
  overflow-y: auto;

  &__title {
    margin-bottom: 10px;
    color: #909399;
    font-size: 13px;
  }
}

.picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .btn-reset {
    min-width: 88px;
    border-radius: 20px;
  }

  .btn-confirm {
    min-width: 88px;
    border-radius: 20px;
  }
}
</style>
